import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { updateOrderAddress, validateAddress } from 'features/checkout/actions';

import styled from 'styled-components';
import _ from 'lodash';

import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';

import countriesRequiringTaxNumber from 'constants/CountriesRequiringTaxNumber';
import DropdownCombobox from './DropdownCombobox';

const getCountryAreas = (country, countries) => {
  return _.result(_.find(countries, { value: country }), 'items', []);
};

const AddressFormElement = styled.form`
  margin-bottom: 24px;
`;

const SubmitButton = styled(Button)`
  margin-top: -24px;
  margin-left: -50px;
  position: relative;
  bottom: -87px;
`;

const AddressFormTextField = ({ ...other }) => (
  <TextField margin="dense" variant="filled" fullWidth {...other} />
);

const AddressForm = ({
  type,
  orderToken,
  orderCountries = [],
  updateOrderAddress,
  validateAddress,
  response,
  validation,
  loading
}) => {
  const fields = {
    city_state_zip: '',
    city_area: '',
    street_address: '',
    phone: '',
    street_address_2: '',
    postal_code: '',
    country_code: '',
    city: '',
    first_name: '',
    country_name: '',
    tax_number: '',
    company_name: '',
    last_name: '',
    country_area: '',
    notification_email: ''
  };

  const [address, setAddress] = useState(fields);
  const [changedFields, setChangedFields] = useState(fields);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const [showCharacterLimit, setShowCharacterLimit] = useState(false);

  const debouncedValidateAddress = useCallback(
    _.debounce(address => validateAddress(address), 100),
    []
  );

  const throttledSetChangedFields = useCallback(
    _.throttle(fields => setChangedFields(fields), 1000, { leading: false }),
    []
  );

  const getFieldErrors = name =>
    (formSubmitted || changedFields[name] !== '') &&
    validation.errors &&
    validation.errors[name]
      ? validation.errors[name].join('\n')
      : false;

  const handleBlur = event => {
    performUpdate(event.target.name, event.target.value);
  };

  const performUpdate = (key, value) => {
    let newAddress = {};
    if (key === 'country_code') {
      newAddress = { ...address, country_area: '', [key]: value };
    } else {
      newAddress = { ...address, [key]: value };
    }

    throttledSetChangedFields({ ...changedFields, [key]: true });
    setAddress(newAddress);
    debouncedValidateAddress(newAddress);
  };

  const handleSubmit = event => {
    event.preventDefault();
    setChangedFields(fields);
    setFormSubmitted(true);
    updateOrderAddress(orderToken, `${type}_address`, null, address);
  };

  const postalCodeExample = _.get(
    validation,
    'suggestions.postal_code_example',
    ''
  );

  return (
    <>
      <AddressFormElement onSubmit={handleSubmit} disabled={loading} noValidate>
        <Grid container spacing={16}>
          <Grid item xs={12}>
            {response.errors && (
              <Typography variant="subtitle2" color="error">
                Please correct the errors below.
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <AddressFormTextField
              name="country_code"
              label="Country"
              value={address.country_code}
              onChange={handleBlur}
              onBlur={handleBlur}
              error={Boolean(getFieldErrors('country_code'))}
              helperText={getFieldErrors('country_code')}
              select
              SelectProps={{ MenuProps: { MenuListProps: { dense: true } } }}
            >
              {orderCountries.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
            </AddressFormTextField>
          </Grid>
          {address.country_code !== 'US' && address.country_code !== '' && (
            <Grid item xs={12}>
              <AddressFormTextField
                name="tax_number"
                label="EIN or Tax Number"
                onBlur={handleBlur}
                error={Boolean(getFieldErrors('tax_number'))}
                helperText={
                  getFieldErrors('tax_number') ||
                  (_.includes(countriesRequiringTaxNumber, address.country_code)
                    ? null
                    : 'Optional, but omission may result in customs delays')
                }
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <AddressFormTextField
              name="company_name"
              label="Company Name"
              onBlur={handleBlur}
              error={Boolean(getFieldErrors('company_name'))}
              helperText={getFieldErrors('company_name')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AddressFormTextField
              name="first_name"
              label="First Name"
              onBlur={handleBlur}
              error={Boolean(getFieldErrors('first_name'))}
              helperText={getFieldErrors('first_name')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AddressFormTextField
              name="last_name"
              label="Last Name"
              onBlur={handleBlur}
              error={Boolean(getFieldErrors('last_name'))}
              helperText={getFieldErrors('last_name')}
            />
          </Grid>
          <Grid item xs={12}>
            <AddressFormTextField
              name="street_address"
              label="Address Line 1"
              onFocus={() => setShowCharacterLimit(true)}
              onBlur={event => {
                handleBlur(event);
                setShowCharacterLimit(false);
              }}
              error={Boolean(getFieldErrors('street_address'))}
              helperText={
                getFieldErrors('street_address') ||
                (showCharacterLimit ? 'Maximum 35 characters allowed' : '')
              }
              multiline
              inputProps={{ maxLength: 35 }}
            />
          </Grid>
          <Grid item xs={12}>
            <AddressFormTextField
              name="street_address_2"
              label="Address Line 2"
              onFocus={() => setShowCharacterLimit(true)}
              onBlur={event => {
                handleBlur(event);
                setShowCharacterLimit(false);
              }}
              error={Boolean(getFieldErrors('street_address_2'))}
              helperText={
                getFieldErrors('street_address_2') ||
                (showCharacterLimit ? 'Maximum 35 characters allowed' : '')
              }
              multiline
              inputProps={{ maxLength: 35 }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            {getCountryAreas(address.country_code, orderCountries).length >
            0 ? (
              <AddressFormTextField
                name="country_area"
                label="State/Province"
                value={address.country_area}
                onChange={handleBlur}
                onBlur={handleBlur}
                error={Boolean(getFieldErrors('country_area'))}
                helperText={getFieldErrors('country_area')}
                select
                SelectProps={{ MenuProps: { MenuListProps: { dense: true } } }}
              >
                {getCountryAreas(address.country_code, orderCountries).map(
                  option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.name}
                    </MenuItem>
                  )
                )}
              </AddressFormTextField>
            ) : (
              <DropdownCombobox
                initialValue={address.country_area}
                onValueChange={val => performUpdate('country_area', val)}
                choices={_.get(
                  validation,
                  'suggestions.country_area_choices',
                  []
                )}
                inputProps={{
                  name: 'country_area',
                  label: 'State/Province',
                  error: Boolean(getFieldErrors('country_area')),
                  helperText: getFieldErrors('country_area'),
                  variant: 'filled',
                  margin: 'dense',
                  fullWidth: true
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <AddressFormTextField
              name="postal_code"
              label="Postal Code"
              onBlur={handleBlur}
              error={Boolean(getFieldErrors('postal_code'))}
              helperText={getFieldErrors('postal_code')}
              placeholder={
                postalCodeExample ? `E.g.  ${postalCodeExample}` : ''
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DropdownCombobox
              onValueChange={val => performUpdate('city', val)}
              choices={_.get(validation, 'suggestions.city_choices', [])}
              inputProps={{
                name: 'city',
                label: 'City',
                error: Boolean(getFieldErrors('city')),
                helperText: getFieldErrors('city'),
                variant: 'filled',
                margin: 'dense',
                fullWidth: true
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DropdownCombobox
              onValueChange={val => performUpdate('city_area', val)}
              choices={_.get(validation, 'suggestions.city_area_choices', [])}
              inputProps={{
                name: 'city_area',
                label: 'District',
                error: Boolean(getFieldErrors('city_area')),
                helperText: getFieldErrors('city_area'),
                variant: 'filled',
                margin: 'dense',
                fullWidth: true
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <AddressFormTextField
              name="notification_email"
              label={`${_.capitalize(type)} Confirmation Email`}
              onBlur={handleBlur}
              error={Boolean(getFieldErrors('notification_email'))}
              helperText={getFieldErrors('notification_email')}
            />
          </Grid>
          <Grid item xs={12}>
            <AddressFormTextField
              name="phone"
              label="Phone Number"
              onBlur={handleBlur}
              error={Boolean(getFieldErrors('phone'))}
              helperText={getFieldErrors('phone')}
            />
          </Grid>
        </Grid>
        <SubmitButton
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
        >
          Use This Address
        </SubmitButton>
      </AddressFormElement>
    </>
  );
};

export default connect(
  state => ({
    response: state.checkout.addressResponse,
    validation: state.checkout.addressValidation,
    loading: state.checkout.addressLoading
  }),
  { updateOrderAddress, validateAddress }
)(AddressForm);
